import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

const noop = () => {};

const Draggable = ({ children, onDragStart = noop, onDragEnd = noop }) => {
  const [domElement, setDomElement] = useState(null)
  const clickTargetRef = useRef(null)
  const handlerRef = useRef(null)

  // Validate children prop
  if (!children || !React.isValidElement(children)) {
    console.error('Draggable: children prop must be a valid React element')
    return null
  }

  const refCallback = useCallback((ref) => {
    if (!ref) {
      setDomElement(null)
      return
    }

    let element = null

    // Handle different ref types
    if (ref instanceof HTMLElement) {
      // Direct DOM element
      element = ref
    } else if (ref && typeof ref === 'object') {
      // Try common patterns for component refs
      if (ref.current instanceof HTMLElement) {
        // useRef pattern
        element = ref.current
      } else if (ref.getDOMElement && typeof ref.getDOMElement === 'function') {
        // Component with getDOMElement method (useImperativeHandle pattern)
        element = ref.getDOMElement()
      } else {
        // Unsupported component type
        console.warn(`⚠️ Component '${ref?.constructor?.name}' doesn't support ref forwarding.`)
        console.warn('Please add ref forwarding or useImperativeHandle for React 19 compatibility.')
        element = null
      }
    }
    setDomElement(element)
  }, [])

  const onMouseDown = useCallback((event) => {
    clickTargetRef.current = event.target
  }, [])

  const handleDragStart = useCallback((event) => {
    event.stopPropagation()

    // if no handler - 'clickTarget' and 'handler' are both 'null' and hence equal
    if (clickTargetRef.current !== handlerRef.current) {
      event.preventDefault()
    } else {
      event.dataTransfer.dropEffect = 'move'
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text', 'dragged')
      onDragStart(event, domElement)
    }
  }, [domElement, onDragStart])

  const handleDragEnd = useCallback((event) => {
    if (event) {
      event.preventDefault()
    }
    clickTargetRef.current = null
    onDragEnd(event, domElement)
  }, [domElement, onDragEnd])

  useEffect(() => {
    if (!domElement) return
    
    // Set draggable attribute
    domElement.setAttribute('draggable', true)
    
    // Add drag event listeners
    domElement.addEventListener('dragstart', handleDragStart, false)
    domElement.addEventListener('dragend', handleDragEnd, false)

    // Find handler element
    const handler = domElement.querySelector('[data-handler=true]')
    handlerRef.current = handler

    if (handler) {
      handler.addEventListener('mousedown', onMouseDown, false)
    }

    // Cleanup function
    return () => {
      if (domElement.removeEventListener) {
        domElement.removeEventListener('dragstart', handleDragStart, false)
        domElement.removeEventListener('dragend', handleDragEnd, false)
      }
      handleDragEnd(null, domElement)
      if (handler && handler.removeEventListener) {
        handler.removeEventListener('mousedown', onMouseDown, false)
      }
      handlerRef.current = null
    }
  }, [domElement, handleDragStart, handleDragEnd, onMouseDown])

  return React.cloneElement(children, { ref: refCallback })
}

Draggable.propTypes = {
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  children: PropTypes.element.isRequired
}

export default Draggable
